import React, { useEffect, useState } from "react";
import { Edit, Trash2, Gift, Bitcoin, Gem, Percent } from "lucide-react";
import { Spin, message } from "antd";
import feeApi from "../../api/feeApi";

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const inputClass =
    "w-full min-w-[8rem] rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2.5 text-[13px] text-white/90 outline-none transition placeholder:text-white/30 focus:border-emerald-400/45 focus:ring-2 focus:ring-emerald-500/15";

export default function FeePage() {
    const [levels, setLevels] = useState([]);
    const [discounts, setDiscounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ onlyGem: false });
    const [isDiscount, setIsDiscount] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [tab, setTab] = useState("levels");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [levelsData, discountsData] = await Promise.all([
                feeApi.feeLevelsApi.getAll(),
                feeApi.feeDiscountsApi.getAll(),
            ]);
            setLevels(levelsData);
            setDiscounts(discountsData);
        } catch {
            message.error("Не удалось загрузить данные");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            if (editingId) {
                isDiscount
                    ? await feeApi.updateDiscount(editingId, form)
                    : await feeApi.updateLevel(editingId, form);
                message.success("Запись обновлена");
            } else {
                isDiscount ? await feeApi.createDiscount(form) : await feeApi.createLevel(form);
                message.success("Запись добавлена");
            }
            setForm({ onlyGem: false });
            setEditingId(null);
            fetchData();
        } catch {
            message.error("Ошибка сохранения");
        }
    };

    const handleDelete = async (id, discount) => {
        try {
            await (discount ? feeApi.deleteDiscount(id) : feeApi.deleteLevel(id));
            message.success("Удалено");
            fetchData();
        } catch {
            message.error("Не удалось удалить");
        }
    };

    const switchTab = (next) => {
        setTab(next);
        setForm(next === "levels" ? { onlyGem: false } : {});
        setEditingId(null);
        setIsDiscount(next === "discounts");
    };

    const renderForm = () => (
        <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
            <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-emerald-500/10 blur-3xl" />
            <div className="relative mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-[14px] font-semibold text-white/90">
                    {editingId ? "Редактирование" : "Новая запись"}
                </h2>
                {editingId && (
                    <button
                        type="button"
                        onClick={() => {
                            setEditingId(null);
                            setForm(tab === "levels" ? { onlyGem: false } : {});
                        }}
                        className="text-[12px] text-white/45 underline-offset-2 hover:text-white/70 hover:underline"
                    >
                        Сбросить
                    </button>
                )}
            </div>
            <div className="relative flex flex-wrap items-end gap-4">
                {tab === "levels" ? (
                    <>
                        <div className="flex min-w-[140px] flex-1 flex-col gap-1.5">
                            <label className="text-[11px] font-medium uppercase tracking-wide text-white/40">
                                Макс. участников
                            </label>
                            <input
                                type="number"
                                className={inputClass}
                                value={form.max ?? ""}
                                onChange={(e) => setForm({ ...form, max: e.target.value === "" ? undefined : +e.target.value })}
                            />
                        </div>
                        <div className="flex min-w-[140px] flex-1 flex-col gap-1.5">
                            <label className="text-[11px] font-medium uppercase tracking-wide text-white/40">
                                Комиссия (USDT)
                            </label>
                            <input
                                type="number"
                                className={inputClass}
                                value={form.fee ?? ""}
                                onChange={(e) => setForm({ ...form, fee: e.target.value === "" ? undefined : +e.target.value })}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-[11px] font-medium uppercase tracking-wide text-white/40">Тип</span>
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, onlyGem: !form.onlyGem })}
                                className={cn(
                                    "inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-[13px] font-medium transition-colors",
                                    form.onlyGem
                                        ? "border-violet-400/35 bg-violet-500/15 text-violet-100 hover:border-violet-400/50"
                                        : "border-amber-400/35 bg-amber-500/15 text-amber-100 hover:border-amber-400/50"
                                )}
                            >
                                {form.onlyGem ? <Gem size={18} /> : <Bitcoin size={18} />}
                                {form.onlyGem ? "Only crystals" : "Crypto + crystals"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex min-w-[140px] flex-1 flex-col gap-1.5">
                            <label className="text-[11px] font-medium uppercase tracking-wide text-white/40">Promo ID</label>
                            <input
                                type="number"
                                className={inputClass}
                                value={form.promoId ?? ""}
                                onChange={(e) =>
                                    setForm({ ...form, promoId: e.target.value === "" ? undefined : +e.target.value })
                                }
                            />
                        </div>
                        <div className="flex min-w-[120px] flex-1 flex-col gap-1.5">
                            <label className="text-[11px] font-medium uppercase tracking-wide text-white/40">Скидка (%)</label>
                            <input
                                type="number"
                                className={inputClass}
                                value={form.discountPercentage ?? ""}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        discountPercentage: e.target.value === "" ? undefined : +e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-[11px] font-medium uppercase tracking-wide text-white/40">Статус</span>
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, isApplied: !form.isApplied })}
                                className={cn(
                                    "inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-[13px] font-medium transition-colors",
                                    form.isApplied
                                        ? "border-emerald-400/35 bg-emerald-500/15 text-emerald-100"
                                        : "border-white/12 bg-white/[0.06] text-white/55"
                                )}
                            >
                                {form.isApplied ? "Применена" : "Не применена"}
                            </button>
                        </div>
                    </>
                )}
                <button
                    type="button"
                    onClick={handleSubmit}
                    className="ml-auto rounded-xl border-0 bg-emerald-500 px-5 py-2.5 text-[13px] font-semibold text-[#070A12] shadow-[0_8px_24px_-8px_rgba(16,185,129,0.5)] transition hover:bg-emerald-400"
                >
                    {editingId ? "Обновить" : "Добавить"}
                </button>
            </div>
        </section>
    );

    const thClass =
        "whitespace-nowrap px-4 py-3.5 text-left text-[12px] font-semibold text-white/55";
    const tdClass = "px-4 py-3 text-[13px] text-white/85";

    const renderTable = (data, discountMode, title) => (
        <div className="mb-2">
            {title && (
                <h3 className="mb-3 flex items-center gap-2 text-[14px] font-semibold text-white/90">{title}</h3>
            )}
            <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
                <table className="w-full min-w-[480px] border-collapse text-left">
                    <thead className="bg-white/[0.05] text-white/60">
                        <tr className="border-b border-white/10">
                            {discountMode ? (
                                <>
                                    <th className={thClass}>Promo ID</th>
                                    <th className={thClass}>Скидка (%)</th>
                                    <th className={thClass}>Применена</th>
                                </>
                            ) : (
                                <>
                                    <th className={thClass}>Макс</th>
                                    <th className={thClass}>Fee USDT</th>
                                    <th className={thClass}>Тип</th>
                                </>
                            )}
                            <th className={cn(thClass, "text-right")}>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={4}
                                    className="px-4 py-10 text-center text-[13px] text-white/40"
                                >
                                    Нет записей
                                </td>
                            </tr>
                        ) : (
                            data.map((item) => (
                                <tr
                                    key={item.id}
                                    className="border-t border-white/[0.06] transition-colors hover:bg-white/[0.04]"
                                >
                                    {discountMode ? (
                                        <>
                                            <td className={cn(tdClass, "text-white/70")}>{item.promoId}</td>
                                            <td className={cn(tdClass, "font-medium text-emerald-300/90")}>
                                                {item.discountPercentage}
                                            </td>
                                            <td className={tdClass}>
                                                <span
                                                    className={cn(
                                                        "inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium",
                                                        item.isApplied
                                                            ? "border-emerald-400/35 bg-emerald-500/12 text-emerald-100"
                                                            : "border-white/12 bg-white/[0.06] text-white/45"
                                                    )}
                                                >
                                                    {item.isApplied ? "Да" : "Нет"}
                                                </span>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className={cn(tdClass, "text-white/70")}>{item.max}</td>
                                            <td className={cn(tdClass, "font-medium text-emerald-300/90")}>{item.fee}</td>
                                            <td className={tdClass}>
                                                {item.onlyGem ? (
                                                    <span className="inline-flex items-center gap-1.5 text-violet-300/90">
                                                        <Gem size={16} /> Crystals
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 text-amber-300/90">
                                                        <Bitcoin size={16} /> Crypto
                                                    </span>
                                                )}
                                            </td>
                                        </>
                                    )}
                                    <td className={cn(tdClass, "text-right")}>
                                        <div className="inline-flex gap-1">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsDiscount(discountMode);
                                                    setEditingId(item.id);
                                                    setForm(item);
                                                }}
                                                className="rounded-lg border border-white/10 bg-white/[0.05] p-2 text-white/70 transition hover:border-emerald-400/30 hover:bg-emerald-500/10 hover:text-emerald-200"
                                                title="Изменить"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(item.id, discountMode)}
                                                className="rounded-lg border border-white/10 bg-white/[0.05] p-2 text-rose-400/90 transition hover:border-rose-400/35 hover:bg-rose-500/10"
                                                title="Удалить"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -left-10 top-0 h-36 w-36 rounded-full bg-amber-400/10 blur-3xl" />
                <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <Percent className="h-5 w-5 text-emerald-300/90" strokeWidth={2} />
                        </span>
                        <div>
                            <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">
                                Комиссии
                            </h1>
                            <p className="text-[12px] text-white/45">
                                Уровни комиссий и скидки по промо
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 rounded-xl border border-white/10 bg-white/[0.05] p-1">
                        <button
                            type="button"
                            onClick={() => switchTab("levels")}
                            className={cn(
                                "rounded-lg px-4 py-2 text-[13px] font-medium transition-colors",
                                tab === "levels"
                                    ? "bg-white/[0.12] text-white"
                                    : "text-white/50 hover:bg-white/[0.06] hover:text-white/80"
                            )}
                        >
                            Fee levels
                        </button>
                        <button
                            type="button"
                            onClick={() => switchTab("discounts")}
                            className={cn(
                                "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-[13px] font-medium transition-colors",
                                tab === "discounts"
                                    ? "bg-white/[0.12] text-white"
                                    : "text-white/50 hover:bg-white/[0.06] hover:text-white/80"
                            )}
                        >
                            <Gift size={16} className="opacity-80" />
                            Promo discounts
                        </button>
                    </div>
                </div>
            </section>

            {renderForm()}

            <section className={cn(shellClass, "flex min-h-0 flex-1 flex-col overflow-hidden")}>
                {loading ? (
                    <div className="flex flex-1 items-center justify-center py-20">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                ) : (
                    <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-4 sm:p-5">
                        {tab === "levels" ? (
                            <>
                                {renderTable(
                                    levels.filter((l) => l.onlyGem),
                                    false,
                                    <>
                                        <Gem size={18} className="text-violet-300/90" /> Only crystals
                                    </>
                                )}
                                {renderTable(
                                    levels.filter((l) => !l.onlyGem),
                                    false,
                                    <>
                                        <Bitcoin size={18} className="text-amber-300/90" /> Crypto + crystals
                                    </>
                                )}
                            </>
                        ) : (
                            renderTable(discounts, true, <>
                                <Gift size={18} className="text-emerald-300/80" /> Promo discounts
                            </>)
                        )}
                    </div>
                )}
            </section>
        </div>
    );
}
